import { useEffect } from 'react'
import { useServices } from 'src/services/ServiceContext'
import { useDispatch, useSelector } from 'react-redux'
import { actions } from 'src/features/singleStory/SingleStorySlice'
import { push, replace } from 'connected-react-router'
import { notificationsEnqueue } from 'src/features/notifications/NotificationsSlice'
import { logPush } from 'src/features/contactUs/ContactUsSlice'
import { setLastArticleId } from 'src/features/benefits/BenefitsSlice'
import FW5MLError from 'src/services/FW5ML/FW5MLError'
import { ANON_MAX_ARTICLES, ANON_MAX_ARTICLES_EXPIRE_DAYS } from 'src/constants'
import {
  getAnonArticleLimitSettings,
  getFreeArticleLimitSettings
} from 'src/features/config/ConfigSelector'
import { actions as paywallActions } from 'src/features/paywall/PaywallSlice'
import { useLazyGetSingleStoryQuery } from 'src/api/endpoints/singleStoryEndpoints'
import { handleNetworkError } from 'src/api/helpers'
import FW5Error, { ANON_ARTICLE_LIMIT_REACHED } from 'src/api/errors/FW5Error'
import { siteName } from 'src/utils/oneCodeBase'
import sharedFunctions from './sharedFunctions'
import { licenseTypes } from 'src/services/FW5ML/parsers/UserParser'
import { VIEWED_ARTICLES_COOKIE } from '../../SingleStoryConstants'

import {
  NOT_ALLOWED,
  SERVER_ERROR,
  ACCESS_DENIED,
  ARTICLE_NOT_PUBLISHED
} from 'src/services/FW5ML/errorCodes'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { STORY_PAGE_TAGS } from 'src/constants/featureFlagConstants'

const useSingleStoryRequest = ({ id }) => {
  const { Session, StoryParser, queryString, Piwik } = useServices([
    'Session',
    'queryString',
    'StoryParser',
    'Piwik'
  ])

  const flags = useFlags()

  const [getSingleStoryQuery] = useLazyGetSingleStoryQuery()
  const { notifyMeSubscribe } = sharedFunctions()

  const location = useSelector(state => state.router.location)
  const anonArticleLimitSettings = useSelector(getAnonArticleLimitSettings())
  const freeArticleLimitConfig = useSelector(getFreeArticleLimitSettings())

  const storyState = useSelector(state => state.singleStoryReducer)

  const paywallVisible = useSelector(
    state => state?.paywallReducer?.paywallVisible
  )
  const paywallSubType = useSelector(state => state?.paywallReducer?.subtype)

  const dispatch = useDispatch()

  /**
   * Checks if the current flow is a Search API flow and handles user redirection if necessary
   *
   * @returns {boolean}
   */
  const isSearchApiFlow = async () => {
    if (!queryString.get('sapi')) return false
    const cookie = 'search_api_cookie'

    if (Session.getCookie(cookie)) {
      Session.removeCookie(cookie)
    }

    const user = await Session.userLoad()

    if (!user) {
      Session.setCookie('search_api_cookie', location.pathname)
      dispatch(replace('/sign-in'))
      return true
    }

    return false
  }

  /**
   * Controls and limits article views for anonymous users based on configured settings.
   *
   * @param {string|number} id - The unique identifier of the article being viewed
   * @param {Object} article - The article object to potentially modify
   *
   * @returns {void}
   */
  const limitAnonViews = async (id, article) => {
    if (anonArticleLimitSettings?.hideArticleLimit) {
      return
    }

    const {
      anonArticleLimit = ANON_MAX_ARTICLES,
      anonArticleLimitExpiry = ANON_MAX_ARTICLES_EXPIRE_DAYS
    } = anonArticleLimitSettings

    const onArticleLimitReached = () => {
      dispatch(
        paywallActions.updatePaywallVisible({
          visible: true,
          type: 'free_only',
          blurArticle: true,
          subtype: 'article_limit'
        })
      )
    }

    const onArticleLimitNotReached = () => {
      dispatch(
        paywallActions.updatePaywallVisible({
          visible: true,
          type: 'free_only',
          subtype: 'article_counter'
        })
      )
    }

    articleLimitCheck(
      id,
      article,
      anonArticleLimit,
      anonArticleLimitExpiry,
      onArticleLimitReached,
      onArticleLimitNotReached
    )
  }

  /**
   * Handles access control and paywall for Plus-only articles by checking the user's license
   *
   * @param {string} [license=''] - The user's license type
   *
   * @returns {void}
   */
  const handlePlusOnlyArticles = async (license = '') => {
    const VALID_LICENSES = [licenseTypes.LICENSE_PLUS]

    if (!VALID_LICENSES.includes(license)) {
      dispatch(
        paywallActions.updatePaywallVisible({
          visible: true,
          type: 'plus_only',
          blurArticle: true,
          subtype: 'plus_article'
        })
      )
    }
  }

  /**
   * Manages and limits the number of free article views for users based on their company type.
   *
   * @param {string} id - The unique identifier of the article being viewed
   * @param {Object} article - The article object containing content information
   * @param {Object} user - The user object containing user and company information
   *
   * @returns {void}
   */
  const limitFreeViews = async (id, article, user) => {
    const userCompanyType = user?.derived_company?.company_type
    const companyTypesToValidate = Object.keys(freeArticleLimitConfig)

    // Check if the user's company type has free article limit settings defined in config
    if (
      !userCompanyType ||
      !companyTypesToValidate?.includes(userCompanyType)
    ) {
      return
    }

    // Default to 50 articles and 30 days expiry if not defined in config
    const {
      limit: freeArticleLimit = 50,
      expiry: freeArticleLimitExpiry = 30
    } = freeArticleLimitConfig[userCompanyType]

    const onArticleLimitReached = () => {
      dispatch(
        paywallActions.updatePaywallVisible({
          visible: true,
          type: 'plus_only',
          blurArticle: true,
          subtype: 'free_article_limit'
        })
      )
    }

    articleLimitCheck(
      id,
      article,
      freeArticleLimit,
      freeArticleLimitExpiry,
      onArticleLimitReached,
      null, // No action needed if limit not reached,
      user
    )
  }

  /**
   * Handles paywall logic for article access based on user type and license
   *
   * @param {string} id - The article ID
   * @param {Object} article - The article object
   * @param {Object|null} user - The user object, null if anonymous
   *
   * @returns {void}
   * @throws {Error} When article access is restricted based on user's license/limits
   */
  const paywallAction = async (id, article, user) => {
    const PLUS_ONLY_ARTICLES = ['Conference Dispatch']

    // Handles anonymous and free users since they don't have plus license
    if (PLUS_ONLY_ARTICLES.includes(article.type)) {
      await handlePlusOnlyArticles(user.license)
      return
    }

    if (!user) {
      await limitAnonViews(id, article)
    }

    // Check if the user is free licensed
    if (user?.license === licenseTypes.LICENSE_FREE) {
      await limitFreeViews(id, article, user)
    }
  }

  /**
   * Removes a specific article ID from the 'VIEWED_ARTICLES_COOKIE cookie.
   * If the cookie doesn't exist, it creates a new one with an empty articles array.
   *
   * @param {string|number} id - The article ID to remove from the viewed articles list
   * @param {Object|null} user - The user object, null if anonymous
   *
   * @returns {void}
   */
  const removeViewedArticleFromCookie = async (id, user = null) => {
    // Skip if user has Plus license
    if (user?.license === licenseTypes.LICENSE_PLUS) {
      return
    }

    let cookieName = VIEWED_ARTICLES_COOKIE

    if (user?.profile_id) {
      cookieName += `_${user.profile_id}`
    }

    let viewedArticles = Session.getCookie(cookieName)
    if (!viewedArticles) {
      viewedArticles = {
        articles: [],
        created: Date.now()
      }
    }
    const { articles, created } = viewedArticles
    const updatedArticles = {
      created,
      articles: articles.filter(article => article !== id)
    }
    Session.setCookie(cookieName, updatedArticles)
  }

  /**
   * Checks and manages article view limits for users based on session cookies.
   *
   * @param {string|number} id - The unique identifier of the article being viewed
   * @param {Object} article - The article object
   * @param {number} articleLimit - The maximum number of articles a user can view
   * @param {number} articleLimitExpiration - The expiration days for the article limit
   * @param {Function|null} onArticleLimitReached - Callback function to execute when article limit is reached
   * @param {Function|null} onArticleLimitNotReached - Callback function to execute when article limit is not reached
   * @param {Object|null} user - The user object, null if anonymous
   *
   * @returns {void}
   */
  const articleLimitCheck = async (
    id,
    article,
    articleLimit,
    articleLimitExpiration,
    onArticleLimitReached = null,
    onArticleLimitNotReached = null,
    user = null
  ) => {
    // Check if article limit and expiration are defined if not, skip validation
    if (!articleLimit || !articleLimitExpiration) return

    let cookieName = VIEWED_ARTICLES_COOKIE

    if (user?.profile_id) {
      cookieName += `_${user.profile_id}`
    }

    let viewedArticles = Session.getCookie(cookieName)

    if (!viewedArticles) {
      viewedArticles = {
        articles: [],
        created: Date.now()
      }
    }

    const { articles, created } = viewedArticles

    if (articles?.length + 1 > articleLimit) {
      if (onArticleLimitReached) {
        await onArticleLimitReached()
      }
      article.body = article.teaser
      return
    }

    if (onArticleLimitNotReached) {
      await onArticleLimitNotReached()
    }

    const updatedArticleArray = [...articles, id]
    const updatedArticles = {
      articles: [...new Set(updatedArticleArray)],
      created
    }

    Session.setCookie(cookieName, updatedArticles, {
      expires: articleLimitExpiration
    })
  }

  /**
   * Tracks user interaction with a single story/article, handling newsletter tracking and page view analytics
   *
   * @param {string} id - The article ID to be tracked
   *
   * @returns {void}
   */
  const trackSingleStory = async id => {
    const nlEventId = queryString.get('nle')
    const nlCampaignId = queryString.get('nlc')
    const nlPosition = queryString.get('nlp')

    const user = await Session.userLoad()

    // the tracking action by default is the ID of the NL
    let nlAction = id

    if (nlEventId) {
      let newQS = queryString.removeKey('nle')
      Piwik.updateCustomVariable('event_id', nlEventId)
      if (nlCampaignId) {
        newQS = queryString.removeKey('nlc', newQS)
        Piwik.updateCustomVariable('project_id', nlCampaignId)
      }
      if (nlPosition) {
        newQS = queryString.removeKey('nlp', newQS)
        if (siteName === 'Reports') {
          nlAction = nlPosition
        }

        if (nlPosition.includes('library-picks')) {
          Piwik.updateCustomVariable('article_id', id)
          Piwik.trackAction('Newsletter/Click/CuratedLibrary/article', {
            fw_access_id: user?.access_group_ids.join(','),
            article_id: id
          })
        }
      }
      dispatch(replace({ search: newQS }))
      Piwik.updateCustomVariable('article_id', id)
      Piwik.track('newsletter', 'article-click', nlAction)
    }

    Piwik.updateCustomVariable('article_id', id)
    const actionType =
      paywallVisible && paywallSubType !== 'article_counter'
        ? 'paywall'
        : 'article'
    Piwik.track('page', 'view', actionType)
  }

  /**
   * Requests and processes a single story article
   *
   * @param {string} id - The unique identifier of the story to request
   *
   * @returns {void}
   * @throws {FW5MLError} When there are middlelayer related errors
   * @throws {FW5Error} When there are general application errors
   */
  const requestSingleStory = async id => {
    dispatch(actions.requestSingleStory(id))
    let user = null

    try {
      if (await isSearchApiFlow()) {
        return
      }

      const { data, isError, error } = await getSingleStoryQuery(id)
      user = await Session.userLoad()

      handleNetworkError({ isError, error })

      const areTagsExtended = flags[STORY_PAGE_TAGS] || false

      const article = await StoryParser.normalizeArticle(
        data?.data,
        // Delete this when feature flag is not needed anymore
        true,
        areTagsExtended
      )

      if (article.type === 'Sponsored Story') {
        window.location.replace(article.url)
        return
      }

      await paywallAction(id, article, user)

      await notifyMeSubscribe()
      await dispatch(actions.successSingleStory(article))
    } catch (e) {
      console.log(e)
      if (e instanceof FW5MLError) {
        const code = e.getCode()
        if (code !== ANON_ARTICLE_LIMIT_REACHED) {
          await removeViewedArticleFromCookie(id, user)
        }
        if (code === NOT_ALLOWED || code === ACCESS_DENIED) {
          dispatch(
            paywallActions.updatePaywallVisible({
              visible: true,
              type: 'plus_only',
              blurArticle: true,
              subtype: 'plus_article'
            })
          )
        }
        if (code === ARTICLE_NOT_PUBLISHED) {
          dispatch(
            notificationsEnqueue({
              message: 'The article you are trying to access is not published.'
            })
          )
          return
        }
        if (code === SERVER_ERROR) {
          dispatch(
            logPush(
              'Single Story',
              `Error while loading article ${id}: ${e.message}`
            )
          )
          dispatch(
            notificationsEnqueue({
              message:
                'Unable to load article. Please refresh the page and try again.'
            })
          )
          Piwik.track('notification', 'error', 'could-not-request-single-story')
          dispatch(push('/contact-us'))
        }
      }
      if (e instanceof FW5Error) {
        const code = e.getCode()
        if (code === ANON_ARTICLE_LIMIT_REACHED) {
          dispatch(setLastArticleId(id))
          dispatch(
            paywallActions.updatePaywallVisible({
              visible: true,
              type: 'free_only',
              blurArticle: true,
              subtype: 'article_limit'
            })
          )
        }
      }
      await dispatch(actions.errorSingleStory(e.message))
    }
  }

  useEffect(() => {
    // Track once the article has finished loading
    const asyncTrackSingleStory = async () => {
      const storyLoaded = !!(
        storyState?.loading === false &&
        (storyState?.story?.id === id || storyState?.error)
      )
      if (storyLoaded) {
        await trackSingleStory(id)
      }
    }
    asyncTrackSingleStory()
  }, [storyState?.loading])

  return {
    requestSingleStory
  }
}

export default useSingleStoryRequest
